import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import './RequestResults.scss';

import RequestResult from './RequestResult';

import { doesRequestRequireFulfillment, hasRequestExpired, isRequestDismissed, isRequestOutstanding } from '../../../Helpers/gifting_helpers';

const RequestResults = props => {
  const {
    talent,
    user,
    requests,
    openArtistModal,
    openChatOverlay,
    downloadGiftingRequests,
    curSearchVal,
    selectedFilter,
    isDownloading,
    resetSearch,
    lookbooks,
    ui
  } = props;

  const orderByCreatedAt = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
  const allGiftingRequests = requests;

  const isSearchingOrFiltering = curSearchVal || selectedFilter;
  const requestFiltering = r => (selectedFilter ? selectedFilter.filter(r) : true);
  const requestsSearchFilter = r => r.user?.name?.toLowerCase().includes(curSearchVal.toLowerCase());
  const filteredGiftingRequests = isSearchingOrFiltering
    ? allGiftingRequests
        .filter(requestFiltering)
        .filter(requestsSearchFilter)
        .sort(orderByCreatedAt)
    : allGiftingRequests.sort(orderByCreatedAt);

  // panel will be split up into 4 regions:
  // 1. requests requiring fulfillment, 2. oustanding requests, 3. fulfilled requests, 4. dismissed requests
  const sections = [
    {
      header: 'Requests Requiring Fulfillment',
      requests: filteredGiftingRequests.filter(doesRequestRequireFulfillment),
      onClick: r => props.openFulfillmentModal({ params: { Request_id: r.id } }),
      largeFormat: true
    },
    {
      header: 'Outstanding Requests',
      requests: filteredGiftingRequests.filter(isRequestOutstanding),
      onClick: () => {},
      largeFormat: false
    },
    {
      header: 'Fulfilled Requests',
      requests: filteredGiftingRequests.filter(r => r.isComplete),
      onClick: () => {},
      largeFormat: false
    },
    {
      header: 'Expired Requests',
      requests: filteredGiftingRequests.filter(hasRequestExpired),
      onClick: () => {},
      largeFormat: false
    },
    {
      header: 'Dismissed Requests',
      requests: filteredGiftingRequests.filter(isRequestDismissed),
      onClick: () => {},
      largeFormat: false
    }
  ];

  const noGiftingRequests = allGiftingRequests.length === 0;
  const noResultsAfterFilterOrSearch = filteredGiftingRequests.length === 0 && isSearchingOrFiltering;

  return (
    <div className='request-results-outer'>
      <div className='request-results-inner'>
        {noGiftingRequests && (
          <div className='empty-results-message'>
            <div className='text'>
              <div>No gifting activity yet.</div>
            </div>
          </div>
        )}
        {noResultsAfterFilterOrSearch && (
          <>
            <div className='empty-results-message'>
              <div className='text'>
                <div>No results found. Clear your search or try a different filter.</div>
                <button className='clear-filters-button' onClick={resetSearch}>
                  Clear Filter and Search
                </button>
              </div>
            </div>
          </>
        )}
        {!noGiftingRequests &&
          !noResultsAfterFilterOrSearch &&
          sections.map((section, index) => {
            const { header, requests, onClick, showChatButton, largeFormat } = section;

            if (requests.length === 0) return null;
            return (
              <div className='results-section' key={header}>
                <div className='results-section-header'>
                  <div className='text'>
                    <div>{header}</div>
                    <span className='request-count'>{requests.length}</span>
                  </div>
                  <div className='spacer'></div>
                  <div className='download-button' onClick={() => downloadGiftingRequests(requests, index)}>
                    <FontAwesomeIcon icon={faDownload} />
                    <button className='btn btn-primary'>{isDownloading === index ? 'Downloading...' : 'Download'}</button>
                  </div>
                </div>
                <div className='results-container'>
                  {requests.map(request => {
                    const lookbook = lookbooks.find(l => l.id === request.Lookbook_id);

                    return (
                      <RequestResult
                        talent={talent}
                        openArtistModal={openArtistModal}
                        openChatOverlay={openChatOverlay}
                        removeSamplesRequest={props.removeSamplesRequest}
                        updateRequest={props.updateRequest}
                        openGiftingModal={props.openGiftingModal}
                        request={request}
                        user={request.user}
                        key={`all-requests-${request.id}`}
                        markSent={onClick}
                        lookbook={lookbook}
                        showChatButton={showChatButton}
                        largeFormat={largeFormat}
                        signedInUser={user}
                        ui={ui}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

RequestResults.propTypes = {
  talent: PropTypes.array,
  ui: PropTypes.object,
  user: PropTypes.object.isRequired,
  requests: PropTypes.array.isRequired,
  lookbooks: PropTypes.array.isRequired,
  openArtistModal: PropTypes.func.isRequired,
  openChatOverlay: PropTypes.func.isRequired,
  openFulfillmentModal: PropTypes.func.isRequired,
  openGiftingModal: PropTypes.func.isRequired,
  downloadGiftingRequests: PropTypes.func.isRequired,
  removeSamplesRequest: PropTypes.func.isRequired,
  updateRequest: PropTypes.func.isRequired,
  isDownloading: PropTypes.number.isRequired,

  curSearchVal: PropTypes.string,
  selectedFilter: PropTypes.object,
  resetSearch: PropTypes.func.isRequired
};

export default RequestResults;
